import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ClipboardPaste,
  ClipboardList,
  FileText,
  CircleAlert,
  FileWarning,
  Sparkles,
  Pencil,
  Trash2,
  Upload,
  Plus,
  Loader2,
} from 'lucide-react'
import { api, messageErreur } from '../../lib/api'
import type { DocumentResume, Venue } from '../../lib/types'
import { Carte, Champ, EtatVide, Message, Modale, Pastille } from '../base'
import VisionneuseDocument from '../VisionneuseDocument'
import * as f from '../../lib/format'
import { ORIGINES_DOCUMENT, PRECISIONS_DATE, TYPES_DOCUMENT } from '@doc/shared'

export default function Documents({
  patientId,
  documents,
  venues,
  ouvrir,
  documentDemande,
  surFermeture,
  surDepot,
}: {
  patientId: string
  documents: DocumentResume[]
  venues: Venue[]
  ouvrir: null | 'document' | 'texte'
  documentDemande?: string | null
  surFermeture: () => void
  surDepot?: (retour: Retour) => void
}) {
  const [formulaire, setFormulaire] = useState<null | 'fichier' | 'texte'>(null)
  const [visionneuse, setVisionneuse] = useState<string | null>(documentDemande ?? null)
  const [edition, setEdition] = useState<DocumentResume | null>(null)
  const naviguer = useNavigate()
  const client = useQueryClient()

  const analyser = useMutation({
    mutationFn: (documentId: string) =>
      api<{ analyse: { id: string } }>(`/api/documents/${documentId}/analyser`, {
        methode: 'POST',
        corps: {},
      }),
    onSuccess: ({ analyse }) => {
      client.invalidateQueries({ queryKey: ['dossier', patientId] })
      naviguer(`/verification/${analyse.id}`)
    },
  })

  const actif = formulaire ?? (ouvrir === 'document' ? 'fichier' : ouvrir === 'texte' ? 'texte' : null)

  return (
    <Carte
      titre="Documents"
      icone={<FileText className="h-[17px] w-[17px] text-bleu-500" />}
      compte={documents.length}
      actions={
        <>
          <button
            className="bouton-discret px-2"
            onClick={() => setFormulaire('texte')}
            title="Coller un texte"
          >
            <ClipboardPaste className="h-4 w-4" />
          </button>
          <button
            className="bouton-discret px-2"
            onClick={() => setFormulaire('fichier')}
            title="Déposer un fichier"
          >
            <Plus className="h-4 w-4" />
          </button>
        </>
      }
    >
      {documents.length === 0 ? (
        <EtatVide
          texte="Aucun document. Un PDF, une photo, un document Word, ou simplement du texte collé."
          action={
            <div className="mt-1 flex gap-2">
              <button className="bouton-doux" onClick={() => setFormulaire('fichier')}>
                <Upload className="h-4 w-4" />
                Déposer un fichier
              </button>
              <button className="bouton-doux" onClick={() => setFormulaire('texte')}>
                <ClipboardPaste className="h-4 w-4" />
                Coller un texte
              </button>
            </div>
          }
        />
      ) : (
        <ul className="divide-y divide-trait pb-2">
          {documents.map((d) => (
            <li key={d.id} className="group flex items-center gap-3 px-5 py-2.5">
              <button className="min-w-0 flex-1 text-left" onClick={() => setVisionneuse(d.id)}>
                <span className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-[14px]">{d.titre}</span>
                  <Pastille ton="bleu">
                    {ORIGINES_DOCUMENT[d.origine as keyof typeof ORIGINES_DOCUMENT]}
                  </Pastille>
                  {(d._count?.provenances ?? 0) > 0 && (
                    <Pastille ton="valide">
                      source de {d._count!.provenances} information
                      {d._count!.provenances > 1 ? 's' : ''}
                    </Pastille>
                  )}
                  {d.fichierPresent === false && (
                    <Pastille ton="attention" icone={<FileWarning className="h-3 w-3" />}>
                      fichier manquant
                    </Pastille>
                  )}
                  <EtatAnalyse document={d} />
                </span>
                <span className="mt-0.5 block truncate text-[12.5px] text-brume">
                  {TYPES_DOCUMENT[d.type as keyof typeof TYPES_DOCUMENT]} ·{' '}
                  {d.dateDocument
                    ? f.dateSelonPrecision(
                        d.dateDocument,
                        (d.precisionDateDocument as keyof typeof PRECISIONS_DATE) ?? 'JOUR',
                      )
                    : 'date du document inconnue'}{' '}
                  · déposé le {f.dateCourte(d.dateAjout)}
                  {d.nbPages ? ` · ${d.nbPages} pages` : ''}
                </span>
              </button>

              {d.analyses?.[0]?.etat === 'A_VERIFIER' ? (
                <Link
                  to={`/verification/${d.analyses[0]!.id}`}
                  className="bouton-doux shrink-0 px-2.5 py-1.5"
                  title="Vérifier ce que l’analyse propose"
                >
                  <ClipboardList className="h-4 w-4" />
                  Vérifier
                </Link>
              ) : (
                !d.analyses?.length &&
                d.fichierPresent !== false && (
                  <button
                    className="bouton-discret shrink-0 px-2 opacity-0 group-hover:opacity-100"
                    onClick={() => analyser.mutate(d.id)}
                    disabled={analyser.isPending}
                    title="Lire ce document et proposer ce qu’il contient"
                  >
                    <Sparkles className="h-4 w-4" />
                  </button>
                )
              )}

              <button
                className="bouton-discret px-2 opacity-0 group-hover:opacity-100"
                onClick={() => setEdition(d)}
                title="Corriger les informations du document"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {actif === 'fichier' && (
        <FormulaireFichier
          cible={{ patientId }}
          venues={venues}
          fermer={() => {
            setFormulaire(null)
            surFermeture()
          }}
          apresEnvoi={surDepot}
        />
      )}
      {actif === 'texte' && (
        <FormulaireTexte
          cible={{ patientId }}
          fermer={() => {
            setFormulaire(null)
            surFermeture()
          }}
          apresEnvoi={surDepot}
        />
      )}
      {visionneuse && (
        <VisionneuseDocument documentId={visionneuse} fermer={() => setVisionneuse(null)} />
      )}
      {edition && (
        <FormulaireMeta
          patientId={patientId}
          document={edition}
          venues={venues}
          fermer={() => setEdition(null)}
        />
      )}
    </Carte>
  )
}

/** Où en est la lecture automatique de ce document. */
function EtatAnalyse({ document }: { document: DocumentResume }) {
  const analyse = document.analyses?.[0]
  if (!analyse) return null
  switch (analyse.etat) {
    case 'EN_ATTENTE':
    case 'EN_COURS':
      return (
        <Pastille ton="bleu" icone={<Loader2 className="h-3 w-3 animate-spin" />}>
          analyse en cours
        </Pastille>
      )
    case 'A_VERIFIER':
      return <Pastille ton="attention">à vérifier</Pastille>
    case 'ERREUR':
      return (
        <Pastille ton="critique" icone={<CircleAlert className="h-3 w-3" />}>
          analyse impossible
        </Pastille>
      )
    default:
      return null
  }
}

const TYPES_SAISISSABLES = Object.entries(TYPES_DOCUMENT)

export type CibleDepot = { patientId: string } | { nouveauDossier: true }

function urlDepot(cible: CibleDepot, texte = false): string {
  const suffixe = texte ? '/texte' : ''
  return 'patientId' in cible
    ? `/api/patients/${cible.patientId}/documents${suffixe}`
    : `/api/documents/nouveau-dossier${suffixe}`
}

export type Retour = { patientId: string; analyseId: string | null }

export function FormulaireFichier({
  cible,
  venues = [],
  fermer,
  apresEnvoi,
}: {
  cible: CibleDepot
  venues?: Venue[]
  fermer: () => void
  apresEnvoi?: (retour: Retour) => void
}) {
  const client = useQueryClient()
  const [fichier, setFichier] = useState<File | null>(null)
  const [titre, setTitre] = useState('')
  const [type, setType] = useState('INDETERMINE')
  const [dateDocument, setDateDocument] = useState('')
  const [venueId, setVenueId] = useState('')
  const [analyser, setAnalyser] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)

  const deposer = useMutation({
    mutationFn: () => {
      const formulaire = new FormData()
      formulaire.append('titre', titre || (fichier?.name ?? 'Document'))
      formulaire.append('type', type)
      if (dateDocument) formulaire.append('dateDocument', dateDocument)
      if (venueId) formulaire.append('venueId', venueId)
      if (!analyser) formulaire.append('analyser', 'non')
      formulaire.append('fichier', fichier!)
      return api<Retour & { document: { id: string }; analyse: { id: string } | null }>(
        urlDepot(cible),
        { methode: 'POST', formulaire },
      )
    },
    onSuccess: (reponse) => {
      client.invalidateQueries({ queryKey: ['dossier', reponse.patientId] })
      client.invalidateQueries({ queryKey: ['documents'] })
      client.invalidateQueries({ queryKey: ['accueil'] })
      fermer()
      apresEnvoi?.({ patientId: reponse.patientId, analyseId: reponse.analyse?.id ?? null })
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  return (
    <Modale
      titre="Déposer un document"
      sousTitre="Le fichier d'origine est conservé tel quel, chiffré sur le serveur."
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => deposer.mutate()}
            disabled={!fichier || deposer.isPending}
          >
            {deposer.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Déposer
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl bg-bleu-50 px-4 py-7 text-center">
          <Upload className="h-6 w-6 text-bleu-400" />
          <span className="text-[14px] font-medium text-bleu-700">
            {fichier ? fichier.name : 'Choisir un fichier'}
          </span>
          <span className="text-[12px] text-brume">
            PDF, photo, document Word, texte, e-mail — 60 Mo au plus
          </span>
          <input
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.tif,.tiff,.doc,.docx,.rtf,.txt,.eml,.msg"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null
              setFichier(f)
              if (f && !titre) setTitre(f.name.replace(/\.[^.]+$/, ''))
            }}
          />
        </label>

        <Champ etiquette="Titre">
          <input className="champ" value={titre} onChange={(e) => setTitre(e.target.value)} />
        </Champ>

        <div className="grid grid-cols-2 gap-3">
          <Champ etiquette="Type de document">
            <select className="champ" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES_SAISISSABLES.map(([cle, libelle]) => (
                <option key={cle} value={cle}>
                  {libelle}
                </option>
              ))}
            </select>
          </Champ>
          <Champ etiquette="Date du document" aide="Celle du document, pas celle du dépôt.">
            <input
              className="champ"
              type="date"
              value={dateDocument}
              onChange={(e) => setDateDocument(e.target.value)}
            />
          </Champ>
        </div>

        {venues.length > 0 && (
          <Champ etiquette="Rattacher à une venue">
            <select className="champ" value={venueId} onChange={(e) => setVenueId(e.target.value)}>
              <option value="">Aucune</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {f.dateCourte(v.date)} — {v.motif ?? v.type}
                </option>
              ))}
            </select>
          </Champ>
        )}

        <label className="flex items-start gap-2.5 rounded-lg bg-bleu-50 px-3.5 py-3 text-[13px]">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-[var(--color-bleu-600)]"
            checked={analyser}
            onChange={(e) => setAnalyser(e.target.checked)}
          />
          <span>
            <span className="font-medium text-bleu-700">Lire et analyser ce document</span>
            <span className="block text-brume">
              Les informations trouvées seront proposées à ta vérification. Rien n’entre au dossier
              sans ta validation. Une analyse coûte quelques centimes.
            </span>
          </span>
        </label>

        {erreur && <Message ton="critique">{erreur}</Message>}
      </div>
    </Modale>
  )
}

export function FormulaireTexte({
  cible,
  fermer,
  apresEnvoi,
}: {
  cible: CibleDepot
  fermer: () => void
  apresEnvoi?: (retour: Retour) => void
}) {
  const client = useQueryClient()
  const [titre, setTitre] = useState('')
  const [texte, setTexte] = useState('')
  const [type, setType] = useState('INDETERMINE')
  const [dateDocument, setDateDocument] = useState('')
  const [analyser, setAnalyser] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)

  const coller = useMutation({
    mutationFn: () =>
      api<Retour & { analyse: { id: string } | null }>(urlDepot(cible, true), {
        methode: 'POST',
        corps: {
          titre: titre || 'Texte collé',
          texte,
          type,
          dateDocument: dateDocument || null,
          analyser,
        },
      }),
    onSuccess: (reponse) => {
      client.invalidateQueries({ queryKey: ['dossier', reponse.patientId] })
      client.invalidateQueries({ queryKey: ['documents'] })
      client.invalidateQueries({ queryKey: ['accueil'] })
      fermer()
      apresEnvoi?.({ patientId: reponse.patientId, analyseId: reponse.analyse?.id ?? null })
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  return (
    <Modale
      titre="Coller un texte"
      sousTitre="Le texte est conservé tel quel : il servira de source aux informations qu'on en tirera."
      large
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => coller.mutate()}
            disabled={!texte.trim() || coller.isPending}
          >
            Conserver dans le dossier
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <textarea
          className="champ min-h-[240px] font-mono text-[13px]"
          autoFocus
          placeholder="Collez ici le compte rendu, le courrier, le résultat…"
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Champ etiquette="Titre">
            <input
              className="champ"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Courrier du confrère"
            />
          </Champ>
          <Champ etiquette="Type">
            <select className="champ" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES_SAISISSABLES.map(([cle, libelle]) => (
                <option key={cle} value={cle}>
                  {libelle}
                </option>
              ))}
            </select>
          </Champ>
          <Champ etiquette="Date du document">
            <input
              className="champ"
              type="date"
              value={dateDocument}
              onChange={(e) => setDateDocument(e.target.value)}
            />
          </Champ>
        </div>

        <label className="flex items-start gap-2.5 rounded-lg bg-bleu-50 px-3.5 py-3 text-[13px]">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-[var(--color-bleu-600)]"
            checked={analyser}
            onChange={(e) => setAnalyser(e.target.checked)}
          />
          <span>
            <span className="font-medium text-bleu-700">Lire et analyser ce texte</span>
            <span className="block text-brume">
              Les informations trouvées seront proposées à ta vérification. Rien n’entre au dossier
              sans ta validation.
            </span>
          </span>
        </label>

        {erreur && <Message ton="critique">{erreur}</Message>}
      </div>
    </Modale>
  )
}

function FormulaireMeta({
  patientId,
  document,
  venues,
  fermer,
}: {
  patientId: string
  document: DocumentResume
  venues: Venue[]
  fermer: () => void
}) {
  const client = useQueryClient()
  const [titre, setTitre] = useState(document.titre)
  const [type, setType] = useState(document.type)
  const [dateDocument, setDateDocument] = useState(f.pourChampDate(document.dateDocument))
  const [precision, setPrecision] = useState(document.precisionDateDocument ?? 'JOUR')
  const [venueId, setVenueId] = useState(document.venueId ?? '')
  const [erreur, setErreur] = useState<string | null>(null)

  const enregistrer = useMutation({
    mutationFn: () =>
      api(`/api/documents/${document.id}`, {
        methode: 'PATCH',
        corps: {
          titre,
          type,
          dateDocument: dateDocument || null,
          precisionDateDocument: precision,
          venueId: venueId || null,
        },
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dossier', patientId] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  const retirer = useMutation({
    mutationFn: () => api(`/api/documents/${document.id}`, { methode: 'DELETE' }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dossier', patientId] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  return (
    <Modale
      titre="Informations du document"
      fermer={fermer}
      piedDePage={
        <>
          <button
            className="bouton-discret mr-auto text-critique"
            onClick={() => {
              if (window.confirm('Retirer ce document du dossier ?')) retirer.mutate()
            }}
          >
            <Trash2 className="h-4 w-4" />
            Retirer
          </button>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => enregistrer.mutate()}
            disabled={enregistrer.isPending}
          >
            Enregistrer
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Champ etiquette="Titre" className="sm:col-span-2">
          <input className="champ" value={titre} onChange={(e) => setTitre(e.target.value)} />
        </Champ>
        <Champ etiquette="Type">
          <select className="champ" value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES_SAISISSABLES.map(([cle, libelle]) => (
              <option key={cle} value={cle}>
                {libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ etiquette="Date du document">
          <input
            className="champ"
            type="date"
            value={dateDocument}
            onChange={(e) => setDateDocument(e.target.value)}
          />
        </Champ>
        <Champ etiquette="Précision de la date">
          <select
            className="champ"
            value={precision}
            onChange={(e) => setPrecision(e.target.value)}
          >
            {Object.entries(PRECISIONS_DATE).map(([cle, libelle]) => (
              <option key={cle} value={cle}>
                {libelle}
              </option>
            ))}
          </select>
        </Champ>
        {venues.length > 0 && (
          <Champ etiquette="Venue rattachée">
            <select className="champ" value={venueId} onChange={(e) => setVenueId(e.target.value)}>
              <option value="">Aucune</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {f.dateCourte(v.date)} — {v.motif ?? v.type}
                </option>
              ))}
            </select>
          </Champ>
        )}

        {erreur && (
          <div className="sm:col-span-2">
            <Message ton="critique">{erreur}</Message>
          </div>
        )}
      </div>
    </Modale>
  )
}
